// src/components/DocumentHub/DocumentHub.js
import React, { useState, useEffect, useRef } from "react";
import "../Table/Table.css";
import toast, { Toaster } from "react-hot-toast";
import Loading from "../Loading";
import { FaPlus, FaEye, FaFolderOpen } from "react-icons/fa";
import { CgSandClock } from "react-icons/cg";
import {
  Box,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchDocuments, uploadDocument, validateDocument, sendDocumentToProcess } from "../../api/documentApi";
import { clearAuthData } from "../../utils/AuthStorage";
import { useNavigate } from "react-router-dom";
import { useErrorToast } from "../../hooks/useErrorToast";
import { useVendor } from "../../utils/Context";
import TablePagination from "@mui/material/TablePagination";
import DataTableLocation from "../DataTable/DataTableLocation";
import DocumentUpload from "./DocumentUpload"; // ✅ new component
import DocumentViewer from './DocumentViewer';
import ManageFolders from "./ManageFolders";
import MoveDocumentModal from "./MoveDocumentModal";
import FolderReport from "./FolderReport";
import { getPermissionByPrefix } from "../../utils/permissions";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography } from "@mui/material";
import { FaInfoCircle } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";

function DocumentHub() {
  const showError = useErrorToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedVendor: selectVendorInContext } = useVendor();
  const [formData, setFormData] = useState({
    vendor_id: selectVendorInContext,
    document_type: "",
    month_period: "",
    reference_number: "",
    notes: "",
    files: [],
  });
  const [addModal, setAddModal] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [validatingRowId, setValidatingRowId] = useState(null);
  const [processingRowId, setProcessingRowId] = useState(null);
  const [moveDoc, setMoveDoc] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const folderRef = useRef();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(200);

  const [filters, setFilters] = useState({
    vendor_id: selectVendorInContext,
    page: 1,
    per_page: 200,
  });
  
  const [filterModel, setFilterModel] = useState({ items: [] });

  const handleViewPDF = (url) => {
  setPdfUrl(url);
  setIframeLoading(true);
  setViewerOpen(true);
};

   const handleClose = () => {
    setFormData({
      vendor_id: selectVendorInContext,
      document_type: "",
      month_period: "",
      reference_number: "",
      notes: "",
      files: [],
    });
    setAddModal(false)
  }

  // ✅ Permissions
const {
  is_view: canViewDocument,
  is_add: canAddDocument,
} = getPermissionByPrefix("upload_document");

const {is_add: canValidateDocument,} = getPermissionByPrefix("validate_document");

const {is_add: canSendToProcess,} = getPermissionByPrefix("send_to_process");


  // 🔍 Fetch Documents
  const { data, isLoading } = useQuery({
    queryKey: ["documents", filters],
    queryFn: () => fetchDocuments(filters),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  // 📤 Upload Document
  const uploadMutation = useMutation({
    mutationFn: uploadDocument,
    onSuccess: (res) => {
      handleClose();
      setIsSubmitting(false);
      if (res.status) {
        toast.success("Document uploaded successfully!");
        setAddModal(false);
        queryClient.invalidateQueries(["documents"]);
      } else {
        toast.error("Upload failed");
      }
    },
    onError: (error) => {
      console.log("error",error)
      setIsSubmitting(false);
      if (error.response?.status === 401) {
        showError("Session Expired! Please Login");
        clearAuthData();
        navigate("/");
      } else {
        showError(error?.response?.data?.message || "Something went wrong.");
      }
    },
  });

  // 🧠 Handle Upload Submit
  const handleUpload = (formData) => {
    if (selectVendorInContext === -1) {
      toast.error("Please select a Vendor");
      return;
    }

    if (!formData.document_type || !formData.files?.length) {
      toast.error("Please fill all required fields and select a file");
      return;
    }

    setIsSubmitting(true);

    const uploadData = new FormData();
    uploadData.append("vendor_id", selectVendorInContext);
    uploadData.append("document_type", formData.document_type);
    uploadData.append("month_period", formData.month_period);

    formData.files.forEach((file) => {
      uploadData.append("file", file);
    });

    uploadData.append("reference_number", formData.reference_number || "");
    uploadData.append("notes", formData.notes || "");
    if (formData.folder_id) {
      uploadData.append("folder_id", formData.folder_id);
    }

    uploadMutation.mutate(uploadData);
  };

  // 🔹 Add this after uploadMutation in your DocumentHub component
  const handleValidate = async (documentId) => {
  try {
    setValidatingRowId(documentId); // ✅ mark row as loading
    const res = await validateDocument(documentId);

    if (res.status) {
      toast.success(res?.message || "Document validated successfully!");
      queryClient.invalidateQueries(["documents"]);
    } else {
      toast.error(res?.message || "Validation failed");
    }
  } catch (error) {
    if (error.response?.status === 401) {
      showError("Session Expired! Please Login");
      clearAuthData();
      navigate("/");
    } else {
      showError(error?.response?.data?.message || "Something went wrong during validation.");
    }
  } finally {
    setValidatingRowId(null); // ✅ stop loading for that row
  }
};

const handleProcess = async (documentId) => {
  try {
    setProcessingRowId(documentId); // 👈 start loader for that row

    const res = await sendDocumentToProcess(documentId);

    if (res.status) {
      toast.success(res?.message || "Document sent for processing!");
      queryClient.invalidateQueries(["documents"]);
    } else {
      toast.error(res?.message || "Processing failed");
    }
  } catch (error) {
    if (error.response?.status === 401) {
      showError("Session Expired! Please Login");
      clearAuthData();
      navigate("/");
    } else {
      showError(error?.response?.data?.message || "Something went wrong during processing.");
    }
  } finally {
    setProcessingRowId(null); // 👈 stop loader
  }
};


  // 🧾 Columns for table
  const columns = [
    { field: "document_type", headerName: "Type", width: 150 },
    { field: "folder", headerName: "Folder", width: 150, valueGetter: (value, row) => row?.folder?.name || "-", renderCell: (params) => params.row.folder?.name || "-" },
    { field: "month_period", headerName: "Month", width: 120 },
    { field: "status", headerName: "Status", width: 150 },
    { field: "reference_number", headerName: "Reference Number", width: 150, renderCell: (params) => params.value || "-" },
    { field: "notes", headerName: "Notes", width: 150, renderCell: (params) => params.value || "-" },
    { field: "uploaded_by", headerName: "Uploaded By", width: 150, valueGetter: (value, row) => row?.uploaded_by?.name || "-", renderCell: (params) => params.row.uploaded_by?.name || "-" },
    { field: "created_at", headerName: "Uploaded On", width: 180, valueGetter: (value, row) => row?.created_at ? new Date(row.created_at).toLocaleString() : "-", renderCell: (params) => new Date(params.row.created_at).toLocaleString() },
{
  field: "actions",
  headerName: "Actions",
  width: 380,
  renderCell: (params) => {
    const status = params.row.status?.toLowerCase();
    const isValidated = status === "validated";
    const isProcessed = status === "processed";
    const isProcessing = processingRowId === params.row.id;

    return (
      <>
        {/* View button always visible if user can view */}
        {canViewDocument && (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleViewPDF(params.row.file_path)}
            sx={{ mr: 0.5, backgroundColor: "rgb(124, 46, 160)", fontSize: "11px", px: 1, py: 0.3, minWidth: 0, textTransform: "none" }}
          >
            <FaEye size={12} style={{ marginRight: 3 }} />
            View
          </Button>
        )}

        {/* Validate */}
        {!isProcessed && !isValidated && canValidateDocument && (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleValidate(params.row.id)}
            disabled={validatingRowId === params.row.id}
            sx={{ mr: 0.5, fontSize: "11px", px: 1, py: 0.3, minWidth: 0, textTransform: "none" }}
          >
            <CgSandClock size={12} style={{ marginRight: 3 }} />
            {validatingRowId === params.row.id ? "Validating..." : "Validate"}
          </Button>
        )}

        {/* Send to Process */}
        {!isProcessed && isValidated && canSendToProcess && (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => handleProcess(params.row.id)}
            disabled={isProcessing}
            sx={{ mr: 0.5, fontSize: "11px", px: 1, py: 0.3, minWidth: 0, textTransform: "none" }}
          >
            {isProcessing ? "Processing..." : "Process"}
          </Button>
        )}

        {/* Set Folder button */}
        {canValidateDocument && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setMoveDoc(params.row)}
            sx={{ backgroundColor: "#7C2EA0", fontSize: "11px", px: 1, py: 0.3, minWidth: 0, textTransform: "none" }}
          >
            <FaFolderOpen size={12} style={{ marginRight: 3 }} />
            Set Folder
          </Button>
        )}
      </>
    );
  },
}


  ];

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vendor_id: selectVendorInContext,
      page,
      per_page: perPage,
    }));
  }, [selectVendorInContext, page, perPage]);

  const documents = data?.data || [];
  
  let filteredDocuments = documents;
  if (filterModel.items && filterModel.items.length > 0) {
    filterModel.items.forEach(item => {
      if (item.value) {
        filteredDocuments = filteredDocuments.filter(doc => {
          let val = doc[item.field];
          if (item.field === 'folder') val = doc.folder?.name;
          if (item.field === 'uploaded_by') val = doc.uploaded_by?.name;
          if (item.field === 'created_at') val = doc.created_at ? new Date(doc.created_at).toLocaleString() : null;
          
          if (!val) return false;
          const strVal = String(val).toLowerCase();
          const searchVal = String(item.value).toLowerCase();
          
          if (item.operator === 'contains') return strVal.includes(searchVal);
          if (item.operator === 'equals') return strVal === searchVal;
          if (item.operator === 'startsWith') return strVal.startsWith(searchVal);
          if (item.operator === 'endsWith') return strVal.endsWith(searchVal);
          if (item.operator === 'isEmpty') return !strVal;
          if (item.operator === 'isNotEmpty') return !!strVal;
          
          return strVal.includes(searchVal);
        });
      }
    });
  }

  const isFiltering = filterModel.items?.some(item => item.value);
  const totalCount = isFiltering ? filteredDocuments.length : (data?.total || 0);

  const handleFilterModelChange = (model) => {
    setFilterModel(model);
    setPage(1);
  };



  
      if (!canViewDocument) {
  return <div>You do not have permission to view this page.</div>;
}
  return (
    <div style={profileStyle}>
      <Toaster position="top-right" reverseOrder={false} />
      {(isSubmitting || isLoading || uploadMutation.isLoading) && <Loading />}

<div className="table-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
    <h2>Document Hub</h2>
    <IconButton onClick={() => setInfoOpen(true)} color="primary" size="small" title="View Information">
      <FaInfoCircle size={20} color="#4A148C" />
    </IconButton>
  </div>
  <div style={{display:"flex", gap:"10px"}}>
  {canAddDocument && (
    <>
      {currentTab === 1 && (
        <button className="upload-button" onClick={() => setAddModal(true)}>
          <FaPlus style={{ marginRight: "5px" }} /> Upload Document
        </button>
      )}
      {currentTab === 0 && (
        <button className="upload-button" onClick={() => folderRef.current?.openAddModal()}>
          <FaPlus style={{ marginRight: "5px" }} /> Create Folder
        </button>
      )}
    </>
  )}
  </div>
</div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 1 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Folders" />
          <Tab label="Documents" />
          <Tab label="Report" />
        </Tabs>
      </Box>

      {currentTab === 0 && (
        <ManageFolders ref={folderRef} />
      )}

      {currentTab === 1 && (
        <>
          <div style={{ marginBottom: "0px" }}>
            <DataTableLocation 
              columns={columns} 
              rows={filteredDocuments} 
              height={400} 
              onFilterModelChange={handleFilterModelChange}
              filterMode="server"
            />
            <TablePagination
              component="div"
              count={totalCount}
              page={page - 1}
              onPageChange={(event, newPage) => setPage(newPage + 1)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(event) => {
                setPerPage(parseInt(event.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50, 100, 200]}
            />
          </div>

          {/* 📤 Separate Upload Component */}
          <DocumentUpload open={addModal} onClose={handleClose} onUpload={handleUpload} isSubmitting={isSubmitting} vendorId={selectVendorInContext} formData={formData} setFormData={setFormData}/>
        </>
      )}

      {currentTab === 2 && (
        <FolderReport />
      )}

      {/* PDF Viewer */}
      <DocumentViewer open={viewerOpen} url={pdfUrl} loading={iframeLoading} onClose={() => setViewerOpen(false)} onIframeLoad={() => setIframeLoading(false)}/>

      {moveDoc && (
        <MoveDocumentModal
          open={!!moveDoc}
          onClose={() => setMoveDoc(null)}
          documentId={moveDoc.id}
          currentFolder={moveDoc.folder}
        />
      )}

<Dialog
  open={infoOpen}
  onClose={() => setInfoOpen(false)}
  maxWidth={false}
  fullWidth={false}
  PaperProps={{
    sx: { width: '650px', maxWidth: '90%' }, // between sm and md
  }}
>
  <DialogTitle
    sx={{
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      pr: 2,
    }}
  >
    📘 Document Hub - Overview
    <IconButton
      aria-label="close"
      onClick={() => setInfoOpen(false)}
      size="small"
    >
      <IoMdClose size={20} />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers>
    <Typography paragraph sx={{ lineHeight: 1.8 }}>
      • A new tab called <strong>“Document Hub”</strong> appears beside the existing “DOCUMENTS”.<br />
      • Clients can upload <strong>PDF files</strong> by selecting a Document Type, Month/Period, and adding optional Reference or Notes.<br />
      • After upload, they see a <strong>success message</strong> and the document status becomes <em>Pending Review</em>.<br />
      • Admins can view all uploaded files, validate them, and send them for processing.<br />
      • <strong>Workflow:</strong> Validate → Send to Process → Moves to main “Documents” tab.<br />
      • Once validated, the file moves to the main <strong>Documents</strong> section for further data extraction.
    </Typography>
  </DialogContent>
</Dialog>


    </div>
  );
}

const profileStyle = {
  // width: "94%",
  background: "#fff",
  borderRadius: "8px",
  marginInline: "16px",
  marginTop: "20px",
  padding: "20px",
};

export default DocumentHub;
